import { useState, useMemo } from 'react';
import { PAGINATION_DEFAULTS } from '../utils/constants';

/**
 * Table data management hook
 * Handles pagination, sorting, and search/filter
 */
export const useTableData = (data, searchFields = [], initialRowsPerPage = PAGINATION_DEFAULTS.rowsPerPage) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Search and filter
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    // Normalize for Turkish characters and case-insensitivity
    const normalize = (str) => {
      if (!str) return '';
      return str
        .toString()
        .normalize('NFD') // Destructure characters (e.g., ü -> u + ¨)
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .toLowerCase()
        .replace(/i/g, 'i') // Extra safety for Turkish i
        .replace(/ı/g, 'i')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .trim();
    };

    const searchWords = normalize(searchTerm).split(/\s+/).filter(Boolean);

    return data.filter(item => {
      // Create a combined searchable string from all searchFields
      const searchableString = searchFields
        .map(field => {
          const value = field.split('.').reduce((obj, key) => obj?.[key], item);
          return value || '';
        })
        .join(' ');
      
      const normalizedItemData = normalize(searchableString);

      // Every word in the search term must be present in the item data
      return searchWords.every(word => normalizedItemData.includes(word));
    });
  }, [data, searchTerm, searchFields]);

  // Sort
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = sortConfig.key.split('.').reduce((obj, key) => obj?.[key], a);
      const bValue = sortConfig.key.split('.').reduce((obj, key) => obj?.[key], b);

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // Handlers
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleRowsPerPageChange = (rows) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  return {
    // Data
    paginatedData,
    filteredData: sortedData,
    totalItems: sortedData.length,

    // Pagination
    currentPage,
    rowsPerPage,
    totalPages,
    startIndex,
    endIndex,

    // Search
    searchTerm,

    // Sort
    sortConfig,

    // Handlers
    handleSearch,
    handleSort,
    handlePageChange,
    handleRowsPerPageChange,
    setCurrentPage,
    setSearchTerm
  };
};
